"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Check } from "lucide-react";
import { useSession } from "@/hooks/use-session";
import { useScrollReveal } from "@/hooks/use-scroll-reveal";
import { cn } from "@/lib/utils";
import posthog from "posthog-js";
import { SignupCta } from "@/components/landing/signup-flow";

const FREE_FEATURES = [1, 2, 3, 4] as const;
const PRO_FEATURES = [1, 2, 3, 4, 5] as const;

function Feature({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <Check className="mt-0.5 h-4 w-4 shrink-0 text-mint" strokeWidth={2.5} />
      <span className="text-sm leading-relaxed text-landing-muted">{children}</span>
    </li>
  );
}

export function LandingPricing() {
  const t = useTranslations("landing");
  const { isAuthenticated } = useSession();
  const headerRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useScrollReveal<HTMLDivElement>({
    animation: "scale-in",
    stagger: true,
    staggerInterval: 80,
  });
  const [yearly, setYearly] = useState(false);

  const track = (plan: string) =>
    posthog.capture("landing_click", {
      element: "pricing_cta",
      plan,
      interval: yearly ? "yearly" : "monthly",
    });

  return (
    <section id="pricing" className="py-24">
      <div className="mx-auto max-w-5xl px-6">
        <div className="mb-12 text-center" ref={headerRef}>
          <span className="mb-4 inline-block rounded-full border border-landing-border bg-landing-card px-4 py-1.5 text-xs font-medium tracking-wide text-mint uppercase">
            {t("pricing.label")}
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-ink sm:text-4xl">
            {t("pricing.title")}
          </h2>
          <p className="mt-3 text-landing-muted">{t("pricing.subtitle")}</p>

          {/* Billing interval */}
          <div className="mt-8 inline-flex rounded-full border border-landing-border bg-landing-card p-1">
            {([false, true] as const).map((isYearly) => (
              <button
                key={String(isYearly)}
                type="button"
                onClick={() => setYearly(isYearly)}
                className={cn(
                  "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                  yearly === isYearly
                    ? "bg-ink/10 text-ink"
                    : "text-landing-muted hover:text-ink",
                )}
              >
                {t(isYearly ? "pricing.yearly" : "pricing.monthly")}
                {isYearly && (
                  <span className="ml-2 rounded-full bg-mint/15 px-2 py-0.5 text-[11px] font-semibold text-mint">
                    {t("pricing.yearlySave")}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-6 sm:grid-cols-2" ref={gridRef}>
          {/* Free */}
          <div className="landing-card flex flex-col rounded-2xl border border-landing-border bg-landing-card p-7">
            <h3 className="text-lg font-semibold text-ink">{t("pricing.freeName")}</h3>
            <p className="mt-1 text-sm text-landing-muted">{t("pricing.freeTagline")}</p>
            <div className="mt-6 flex items-baseline gap-1">
              <span className="text-4xl font-bold tracking-tight text-ink">
                {t("pricing.freePrice")}
              </span>
              <span className="text-sm text-landing-muted">{t("pricing.perMonth")}</span>
            </div>
            <ul className="mt-7 flex-1 space-y-3">
              {FREE_FEATURES.map((n) => (
                <Feature key={n}>{t(`pricing.freeFeature${n}`)}</Feature>
              ))}
            </ul>
            <SignupCta
              onPress={() => track("free")}
              className="mt-8 inline-flex items-center justify-center rounded-full border border-landing-border px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-ink/5"
            >
              {t("pricing.freeCta")}
            </SignupCta>
          </div>

          {/* Pro */}
          <div className="landing-card relative flex flex-col rounded-2xl border border-mint/40 bg-landing-card p-7">
            <span className="absolute -top-3 right-6 rounded-full bg-mint px-3 py-1 text-[11px] font-semibold text-[#052e25]">
              {t("pricing.proBadge")}
            </span>
            <h3 className="text-lg font-semibold text-ink">{t("pricing.proName")}</h3>
            <p className="mt-1 text-sm text-landing-muted">{t("pricing.proTagline")}</p>
            <div className="mt-6 flex items-baseline gap-1">
              <span className="text-4xl font-bold tracking-tight text-ink">
                {t(yearly ? "pricing.proPriceYearly" : "pricing.proPrice")}
              </span>
              <span className="text-sm text-landing-muted">
                {t(yearly ? "pricing.perYear" : "pricing.perMonth")}
              </span>
            </div>
            <p className="mt-1 h-5 text-xs text-landing-muted">
              {yearly ? t("pricing.billedYearly") : ""}
            </p>
            <ul className="mt-6 flex-1 space-y-3">
              {PRO_FEATURES.map((n) => (
                <Feature key={n}>{t(`pricing.proFeature${n}`)}</Feature>
              ))}
            </ul>
            <SignupCta
              onPress={() => track("pro")}
              className="mt-8 inline-flex items-center justify-center rounded-full bg-mint px-6 py-3 text-sm font-semibold text-[#052e25] transition-all hover:bg-mint-dark active:scale-95"
            >
              {t("pricing.proCta")}
            </SignupCta>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-landing-muted">
          {t("pricing.note")}
        </p>
      </div>
    </section>
  );
}
